import React from 'react';
import { Theme } from '@mui/material';
import LocationSearchingIcon from '@mui/icons-material/LocationSearching';
import { ErrorOutline, InfoOutline, Memory, Psychology } from '@mui/icons-material';
import { ApiActivityType, ChatSenderType } from 'types/types';

export const getMessageStyle = (
  theme: Theme,
  type: ApiActivityType | ChatSenderType | 'error',
  isMobile: boolean
): Record<string, string> => {
  const defaultRadius = '16px';
  const defaultStyle = {
    padding: theme.spacing(1, 2),
    fontSize: '0.875rem',
    alignSelf: 'flex-start',
    maxWidth: '100%',
    minWidth: '100%',
    height: 'fit-content',
    '& > *': { color: 'inherit', overflow: 'hidden', m: 0 },
    '& > :last-child': { mb: 0, m: 0, p: 0 },
  };
  const styles: Record<string, Record<string, string | object | number | undefined> | string> = {
    assistant: {
      ...defaultStyle,
      backgroundColor: theme.palette.primary.main,
      border: `1px solid ${theme.palette.secondary.main}`,
      borderRadius: `${defaultRadius} ${defaultRadius} ${defaultRadius} 0`,
      color: theme.palette.primary.contrastText,
    },
    content: {
      ...defaultStyle,
      backgroundColor: '#F5F2EA',
      border: `1px solid ${theme.palette.custom.highlight}`,
      borderRadius: 0,
      alignSelf: 'center',
      color: theme.palette.text.primary,
      padding: '8px 8px',
      marginBottom: '0px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      fontSize: '0.9rem',
      lineHeight: '1.3',
      fontFamily: theme.typography.fontFamily,
    },
    error: {
      ...defaultStyle,
      backgroundColor: '#F8E7E7',
      border: `1px solid #D83A3A`,
      borderRadius: defaultRadius,
      maxWidth: '90%',
      minWidth: '90%',
      alignSelf: 'center',
      color: '#8B2525',
      padding: '10px 16px',
      boxShadow: '0 1px 3px rgba(216, 58, 58, 0.15)',
    },
    information: {
      ...defaultStyle,
      backgroundColor: '#BFD8D8',
      border: `1px solid ${theme.palette.secondary.main}`,
      borderRadius: defaultRadius,
      color: theme.palette.text.primary,
      opacity: 0.95,
      fontSize: isMobile ? '0.75rem' : '0.85rem',
    },
    qualifications: {
      ...defaultStyle,
      backgroundColor: theme.palette.primary.light,
      border: `1px solid ${theme.palette.secondary.main}`,
      borderRadius: `${defaultRadius} ${defaultRadius} ${defaultRadius} 0`,
      color: theme.palette.primary.contrastText,
    },
    status: {
      ...defaultStyle,
      backgroundColor: 'rgba(74, 122, 125, 0.15)',
      border: `1px solid ${theme.palette.secondary.light}`,
      borderRadius: '4px',
      maxWidth: '75%',
      minWidth: '75%',
      alignSelf: 'center',
      color: theme.palette.secondary.dark,
      fontWeight: 500,
      fontSize: '0.95rem',
      padding: '8px 12px',
      opacity: 0.9,
      transition: 'opacity 0.3s ease-in-out',
    },
    system: {
      ...defaultStyle,
      backgroundColor: '#EDEAE0',
      border: `1px dashed ${theme.palette.custom.highlight}`,
      borderRadius: defaultRadius,
      maxWidth: '90%',
      minWidth: '90%',
      alignSelf: 'center',
      color: theme.palette.text.primary,
      fontStyle: 'italic',
    },
    user: {
      ...defaultStyle,
      backgroundColor: theme.palette.background.default,
      border: `1px solid ${theme.palette.custom.highlight}`,
      borderRadius: `${defaultRadius} ${defaultRadius} 0 ${defaultRadius}`,
      alignSelf: 'flex-end',
      color: theme.palette.primary.main,
    },
    'fact-check': 'qualifications',
    generating: 'status',
    'job-description': 'content',
    'job-requirements': 'qualifications',
    info: 'information',
    preparing: 'status',
    processing: 'status',
    resume: 'content',
    searching: 'status',
    streaming: 'response',
    thinking: 'status',
  };

  for (const [key, value] of Object.entries(styles)) {
    if (typeof value === 'string') styles[key] = styles[value];
  }

  return styles[type] as Record<string, string>;
};

export const getMessageIcon = (
  activityType: ApiActivityType | ChatSenderType | 'error'
): React.ReactNode | null => {
  const icons: Record<string, React.ReactNode> = {
    error: <ErrorOutline color="error" />,
    generating: <LocationSearchingIcon />,
    information: <InfoOutline color="info" />,
    preparing: <LocationSearchingIcon />,
    processing: <LocationSearchingIcon />,
    system: <Memory />,
    thinking: <Psychology />,
    tooling: <LocationSearchingIcon />,
  };
  return icons[activityType] || null;
};
